import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { checkFullTokenValidation, getUserRole, getAccessToken, refreshAccessToken } from 'utils/auth/auth';
import { AppDispatch } from 'config/redux/store';
import Spinner from 'components/common/Spinner';

// Define interface for component props
interface ProtectedRouteProps {
  allowedRoles?: string[];
}

// Define component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles = [] }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  useEffect(() => {
    const validate = async () => {
      // Check if just logged in to skip redundant validation
      if (localStorage.getItem('justLoggedIn') === 'true') {
        setIsAuthorized(true);
        return;
      }

      let token = getAccessToken();

      // Cross-tab bootstrap: a fresh tab (e.g. user clicked an email link)
      // has no sessionStorage token even if the original tab is logged in.
      // Use the cross-tab refresh-token cookie to silently get a new access
      // token before treating the user as unauthenticated.
      if (!token) {
        try {
          const refreshed = await refreshAccessToken(dispatch);
          if (refreshed) token = refreshed;
        } catch {
          // Fall through — isAuthorized=false below
        }
      }

      if (!token) {
        setIsAuthorized(false);
        return;
      }

      const isValid = await checkFullTokenValidation(token, dispatch);

      if (!isValid) {
        setIsAuthorized(false);
        return;
      }

      const userRole = getUserRole();

      // Case-insensitive role check (also handles spaces in role names like "Super Admin")
      if (allowedRoles.length > 0) {
        if (!userRole) {
          setIsAuthorized(false);
          return;
        }
        // Normalize by removing spaces and converting to lowercase
        const normalizedUserRole = userRole.toLowerCase().replace(/\s+/g, '');
        const hasRole = allowedRoles.some(role => role.toLowerCase().replace(/\s+/g, '') === normalizedUserRole);
        if (!hasRole) {
          setIsAuthorized(false);
          return;
        }
      }

      setIsAuthorized(true);
    };

    validate();
    // Re-run on path change so a navigation within the SPA still re-validates.
    // We intentionally use location.pathname (string) so deps stay primitive.
  }, [dispatch, allowedRoles, location.pathname]);

  if (isAuthorized === null) {
    return <Spinner size="large" text="Loading..." fullPage />;
  }

  // Preserve the intended URL so Login can navigate back after a successful login
  return isAuthorized
    ? <Outlet />
    : <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
};

export default ProtectedRoute;